import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/** `sync-add-to-cart` hosts currently intersecting the viewport (hide sticky bar while any are visible). */
const syncAddToCartAnchorsInView = new Set();

function refreshSyncAddToCartStickySuppression() {
  document.body.classList.toggle('sync-add-to-cart-anchor-visible', syncAddToCartAnchorsInView.size > 0);
}

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean,
 *   whenFinished: Promise<void>
 * }} FlyToCart
 */

/**
 * @typedef {Object} PuppetAddToCartRefs
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 */

/**
 * Puppet-clicks the main PDP `product-form` add button (fly-to-cart, quantity in label).
 *
 * @extends {Component<PuppetAddToCartRefs & { stickyBar?: HTMLElement }>}
 */
class AddToCartPuppetBase extends Component {
  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  connectedCallback() {
    super.connectedCallback();

    this.wireTargetButton();
    this.onPuppetConnected();

    const { signal } = this.#abortController;
    const variantRoot = this.getVariantEventTarget();
    variantRoot?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    variantRoot?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @returns {EventTarget | null} */
  getVariantEventTarget() {
    return this.closest('.shopify-section');
  }

  onPuppetConnected() {}

  wireTargetButton() {
    const productForm = this.getProductForm();
    if (!productForm) return;
    this.targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (sectionElement) {
      const sectionId = sectionElement.id.replace('shopify-section-', '');
      const scoped = document.querySelector(
        `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
      );
      if (scoped) return scoped;
    }

    return document.querySelector(`product-form-component[data-product-id="${productId}"]`);
  }

  /**
   * @param {CustomEvent & { detail: { data: { productId: string } } }} event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;
    this.applyVariantMorph(event);
  };

  /**
   * @param {CustomEvent} _event
   */
  applyVariantMorph(_event) {}

  handleAddToCartClick = () => {
    if (!this.targetAddToCartButton) return;

    const productForm = this.getProductForm();
    if (
      productForm &&
      typeof /** @type {{ artieShouldBlockAddForPackFields?: () => boolean }} */ (productForm).artieShouldBlockAddForPackFields ===
        'function' &&
      /** @type {{ artieShouldBlockAddForPackFields: () => boolean }} */ (productForm).artieShouldBlockAddForPackFields()
    ) {
      return;
    }

    const puppetBtn = this.refs.addToCartButton;
    if (!puppetBtn || puppetBtn.disabled) return;

    const addToCartComponent = /** @type {HTMLElement | null} */ (
      this.targetAddToCartButton.closest('add-to-cart-component')
    );
    const animationEnabled = addToCartComponent?.dataset.addToCartAnimation === 'true';
    const image = addToCartComponent?.dataset.productVariantMedia;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    /** @type {Promise<void>} */
    let flyFinished = Promise.resolve();
    if (animationEnabled && cartIcon && image) {
      const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

      const flyToCartClass = this.targetAddToCartButton.classList.contains('quick-add__button')
        ? 'fly-to-cart--quick'
        : 'fly-to-cart--main';

      flyToCartElement.classList.add(flyToCartClass);
      flyToCartElement.style.setProperty('background-image', `url(${image})`);
      flyToCartElement.style.setProperty('--start-opacity', '0');
      flyToCartElement.source = puppetBtn;
      flyToCartElement.destination = cartIcon;

      document.body.appendChild(flyToCartElement);
      flyFinished = flyToCartElement.whenFinished;
    }

    const buttonFinished = this.#animatePuppetButton();
    const presentationPromise = Promise.all([flyFinished, buttonFinished]).then(() => {});

    if (productForm) {
      /** @type {HTMLElement & { __stickyCartPresentationForNextAdd?: Promise<void> }} */ (productForm).__stickyCartPresentationForNextAdd =
        presentationPromise;
    }

    this.targetAddToCartButton.dataset.puppet = 'true';
    this.targetAddToCartButton.click();
  };

  #animatePuppetButton = async () => {
    const puppetBtn = this.refs.addToCartButton;
    if (!puppetBtn) return;

    if (puppetBtn.dataset.added !== 'true') {
      puppetBtn.dataset.added = 'true';
    }
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    try {
      await yieldToMainThread();
      await onAnimationEnd(puppetBtn);
    } catch {
      puppetBtn.removeAttribute('data-added');
      return;
    }

    this.#resetTimeout = setTimeout(() => {
      puppetBtn.removeAttribute('data-added');
    }, 800);
  };

  /**
   * @param {CustomEvent & { detail: { resource?: { id?: string } } }} event
   */
  #handleVariantSelected = (event) => {
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  handleVariantUnavailable() {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */ (option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  }

  /**
   * @param {CustomEvent} _event
   */
  #handleCartAddComplete = (_event) => {
    if (this.targetAddToCartButton) {
      this.targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * @param {QuantitySelectorUpdateEvent} event
   */
  #handleQuantityUpdate = (event) => {
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.updateButtonText();
  };

  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.updateButtonText();
  }

  updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    quantityNumber.textContent = this.#currentQuantity.toString();

    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }

  /**
   * @param {HTMLButtonElement} newButton
   */
  syncButtonFromMainAddToCart(newButton) {
    const btn = /** @type {HTMLButtonElement} */ (this.refs.addToCartButton);
    if (!btn) return;

    btn.disabled = newButton.disabled;

    const oldText = btn.querySelector('.add-to-cart-text');
    const newText = newButton.querySelector('.add-to-cart-text');
    if (oldText && newText) {
      morph(oldText, newText, { childrenOnly: true });
    }

    const content = btn.querySelector('.add-to-cart-text__content');
    if (content && !content.querySelector('[ref="quantityDisplay"]')) {
      const qtyWrap = document.createElement('span');
      qtyWrap.setAttribute('ref', 'quantityDisplay');
      qtyWrap.style.display = 'none';
      qtyWrap.append(document.createTextNode(' ('));
      const num = document.createElement('span');
      num.setAttribute('ref', 'quantityNumber');
      qtyWrap.append(num, document.createTextNode(')'));
      content.appendChild(qtyWrap);
    }

    btn.type = 'button';
    btn.removeAttribute('name');
    btn.setAttribute('on:click', '/handleAddToCartClick');
  }
}

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar
 * @property {HTMLButtonElement} addToCartButton
 * @property {HTMLElement} quantityDisplay
 * @property {HTMLElement} quantityNumber
 */

/**
 * @extends {AddToCartPuppetBase}
 */
class StickyAddToCartComponent extends AddToCartPuppetBase {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {boolean} */
  #isStuck = false;

  onPuppetConnected() {
    this.#showStickyBar();
  }

  #showStickyBar() {
    const stickyBar = this.refs.stickyBar;
    if (!stickyBar) return;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * @param {CustomEvent} event
   */
  applyVariantMorph(event) {
    const variant = event.detail.resource;

    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    const { stickyBar } = this.refs;
    if (!stickyBar) return;

    morph(stickyBar, newStickyBar, { childrenOnly: true });

    stickyBar.setAttribute('data-stuck', 'true');
    this.dataset.variantAvailable = variantAvailable;

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    const productForm = this.getProductForm();
    if (productForm) {
      this.targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    if (variant == null) {
      this.handleVariantUnavailable();
    }
    this.updateButtonText();
  }
}

/**
 * Reusable block: same puppet behavior as sticky ATC, for any placement on the PDP.
 *
 * @extends {AddToCartPuppetBase}
 */
class SyncAddToCartComponent extends AddToCartPuppetBase {
  requiredRefs = ['addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #stickySuppressObserver = null;

  connectedCallback() {
    super.connectedCallback();
    this.#setupStickyBarSuppressionObserver();
  }

  disconnectedCallback() {
    this.#teardownStickyBarSuppressionObserver();
    super.disconnectedCallback();
  }

  #setupStickyBarSuppressionObserver() {
    if (this.dataset.hideStickyWhenVisible === 'false') return;

    this.#stickySuppressObserver = new IntersectionObserver(this.#onStickyBarSuppressionIntersect, {
      threshold: [0, 0.01],
      root: null,
      rootMargin: '0px',
    });
    this.#stickySuppressObserver.observe(this);
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #onStickyBarSuppressionIntersect = (entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        syncAddToCartAnchorsInView.add(this);
      } else {
        syncAddToCartAnchorsInView.delete(this);
      }
    }
    refreshSyncAddToCartStickySuppression();
  };

  #teardownStickyBarSuppressionObserver() {
    syncAddToCartAnchorsInView.delete(this);
    refreshSyncAddToCartStickySuppression();
    this.#stickySuppressObserver?.disconnect();
    this.#stickySuppressObserver = null;
  }

  /** @returns {EventTarget | null} */
  getVariantEventTarget() {
    return document;
  }

  /**
   * @param {CustomEvent} event
   */
  applyVariantMorph(event) {
    const variant = event.detail.resource;
    const html = event.detail.data.html;

    const newSticky = html.querySelector('sticky-add-to-cart');
    let newButton = /** @type {HTMLButtonElement | null} */ (
      newSticky?.querySelector('[ref="addToCartButton"]')
    );
    let variantAvailable = newSticky?.dataset.variantAvailable;

    if (!newButton) {
      newButton = html.querySelector('product-form-component [ref="addToCartButton"]');
      variantAvailable = newButton && !newButton.disabled ? 'true' : 'false';
    }

    if (!newButton || !this.refs.addToCartButton) return;

    if (newSticky) {
      morph(this.refs.addToCartButton, newButton, { ...MORPH_OPTIONS, childrenOnly: false });
    } else {
      this.syncButtonFromMainAddToCart(newButton);
    }

    if (variantAvailable !== undefined) {
      this.dataset.variantAvailable = variantAvailable;
    }

    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    this.wireTargetButton();

    if (variant == null) {
      this.handleVariantUnavailable();
    }
    this.updateButtonText();
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

if (!customElements.get('sync-add-to-cart')) {
  customElements.define('sync-add-to-cart', SyncAddToCartComponent);
}
